import { normalizeApiResponse } from '../../utils/responseBuilder';
import type { ApiRequestError, ApiRequestOptions, ApiSuccess, SetupStatus } from '../../types/api/envelope';

/**
 * Base URL for API calls.
 * - Empty string: same-origin (production nginx or Vite dev proxy for /api).
 * - Absolute URL: direct API host (e.g. http://localhost:3001).
 */
export function getApiBaseUrl(): string {
  const raw = import.meta.env.VITE_API_URL;
  if (raw === '' || raw === '/') return '';
  if (raw === undefined || raw === null) return '';
  return String(raw).replace(/\/$/, '');
}

/**
 * Optional public API origin for GitHub inbound webhook payload URLs when the SPA host
 * differs from the host GitHub must POST to.
 */
export function getWebhookPublicApiOrigin(): string {
  const raw = import.meta.env.VITE_WEBHOOK_PUBLIC_API_URL;
  if (raw == null || String(raw).trim() === '') return '';
  return String(raw).trim().replace(/\/$/, '');
}

const API_URL = getApiBaseUrl();

let onUnauthorized: (() => void) | null = null;
let onGitHubSetupRequired: (() => void) | null = null;

export function setUnauthorizedHandler(callback: () => void): void {
  onUnauthorized = callback;
}

export function setGitHubSetupRequiredHandler(callback: () => void): void {
  onGitHubSetupRequired = callback;
}

export function getToken(): string | null {
  return localStorage.getItem('token');
}

export function setToken(token: string): void {
  localStorage.setItem('token', token);
}

function removeToken(): void {
  localStorage.removeItem('token');
}

export function clearAuth(): void {
  removeToken();
  localStorage.removeItem('user');
  localStorage.removeItem('isAuthenticated');
}

export function notifySessionExpired(): void {
  clearAuth();
  if (typeof onUnauthorized === 'function') onUnauthorized();
}

export function toQueryString(params: Record<string, unknown> | null | undefined): string {
  if (!params || typeof params !== 'object') return '';
  const filtered = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
  );
  const s = new URLSearchParams(
    Object.fromEntries(
      Object.entries(filtered).map(([k, v]) => [k, String(v)])
    )
  ).toString();
  return s ? `?${s}` : '';
}

export function getApiUrl(): string {
  return API_URL;
}

export async function apiRequest<T = unknown>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const token = getToken();

  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string> | undefined),
  };

  if (token && !options.skipAuth) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const timeoutMs = options.timeoutMs ?? 30000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
      signal: options.signal ?? controller.signal,
    });
    clearTimeout(timeoutId);

    if (response.status === 401 && token) {
      notifySessionExpired();
      throw new Error('Session expired. Please log in again.');
    }

    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    if (!isJson) {
      await response.text();
      if (!response.ok) {
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }
      throw new Error('Invalid response format');
    }

    const raw = await response.json();

    if (!response.ok) {
      const code = raw.code || (raw.data && raw.data.code);
      if (code === 'FEATURE_DISABLED') {
        const err = new Error('This feature is not available for your organisation.') as ApiRequestError;
        err.code = 'FEATURE_DISABLED';
        throw err;
      }
      if (
        (code === 'GITHUB_INTEGRATION_REQUIRED' || code === 'GITHUB_BAD_CREDENTIALS') &&
        typeof onGitHubSetupRequired === 'function'
      ) {
        onGitHubSetupRequired();
      }
      const normalizedError = normalizeApiResponse(raw);
      const errorBody = normalizedError as { message?: string; code?: string; meta?: Record<string, unknown> };
      const message =
        errorBody.message ||
        (raw as { error?: string }).error ||
        (raw as { message?: string }).message ||
        `Request failed with status ${response.status}`;
      const err = new Error(message) as ApiRequestError;
      err.code = code || errorBody.code;
      err.meta = errorBody.meta || (raw as { meta?: Record<string, unknown> }).meta || undefined;
      err.status = response.status;
      throw err;
    }

    const normalized = normalizeApiResponse<T>(raw) as ApiSuccess<T>;
    if (
      normalized.meta != null &&
      normalized.data !== undefined &&
      typeof normalized.data === 'object' &&
      normalized.data !== null
    ) {
      return { ...normalized.data, meta: normalized.meta } as T;
    }
    return (normalized.data !== undefined ? normalized.data : (normalized as unknown as T)) as T;
  } catch (error: unknown) {
    clearTimeout(timeoutId);
    const err = error as Error & { name?: string };
    if (err.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    if (err.name === 'TypeError' && (err as Error).message?.includes('fetch')) {
      throw new Error('Unable to connect to server. Please check if the backend is running.');
    }
    throw error;
  }
}

export async function getSetupStatus(): Promise<SetupStatus> {
  const res = await fetch(`${API_URL}/api/auth/setup-status`, {
    method: 'GET',
    headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Error).message || err.error || `Setup status failed: ${res.status}`);
  }
  const data = await res.json();
  const payload = data.data ?? data;
  return {
    hasUsers: !!payload.hasUsers,
    requiresOrganisationSetup: payload.requiresOrganisationSetup === true,
    requiresGitHubSetup: payload.requiresGitHubSetup === true,
  };
}
